
// frontend/app/exhibit/v2/screens/HubScreen.js

import React, { useEffect, useState } from "react";
import { getExhibitAttemptStats } from "../../api/aiExhibitAnalyticsClient";
import { getRealOrAIPhotoRound } from "../../api/aiExhibitContentClient";
import { Pressable, StyleSheet, View } from "react-native";
import { LocalizedText as Text } from "../i18n/ExhibitLocaleContext";
import { tx } from "../i18n/exhibitLocale";

const PRIMARY_GAMES = [
  {
    key: "teach",
    icon: "🧪",
    title: "Teach a Machine",
    time: "3 min",
  },
  {
    key: "outsmart",
    icon: "🎯",
    title: "Outsmart the AI",
    time: "2 min",
  },
  {
    key: "next-word",
    icon: "🔤",
    title: "Next-Word Line-Up",
    time: "2 min",
  },
  {
    key: "today",
    icon: "⚡",
    title: "AI Today",
    time: "2 min",
  },
  {
    key: "future",
    icon: "🌱",
    title: "My Future With AI",
    time: "2 min",
  },
  {
    key: "train-lab",
    icon: "🧬",
    title: "What Helps AI Learn?",
    time: "2 min",
  },
];

const MORE_GAMES = [
  { key: "ask", icon: "💬", title: "Ask about AI" },
  { key: "coach", icon: "🧭", title: "Answer or Coach?" },
  { key: "where-ai", icon: "🔍", title: "Where's the AI?" },
  { key: "bluff", icon: "🎙️", title: "Confidence Bluff" },
  { key: "feed", icon: "📱", title: "The Feed Machine" },
  { key: "dial", icon: "🎛️", title: "The Dial Wall" },
  { key: "relay", icon: "🏁", title: "Human vs. Machine" },
];

function PrimaryTile({ item, onPress, language = "en" }) {
  return (
    <Pressable
      onPress={() => onPress(item.key)}
      style={({ pressed }) => [
        styles.primaryTile,
        pressed && styles.pressed,
      ]}
    >
      <Text style={styles.primaryIcon}>{item.icon}</Text>

      <Text style={styles.primaryTitle}>
        {tx(language, item.title)}
      </Text>

      <Text style={styles.primaryTime}>
        {item.time}
      </Text>
    </Pressable>
  );
}

function SmallTile({ item, onPress, language = "en" }) {
  return (
    <Pressable
      onPress={() => onPress(item.key)}
      style={({ pressed }) => [
        styles.smallTile,
        pressed && styles.pressed,
      ]}
    >
      <Text style={styles.smallIcon}>{item.icon}</Text>

      <Text style={styles.smallTitle}>
        {tx(language, item.title)}
      </Text>
    </Pressable>
  );
}

export default function HubScreen({
  tier = "hs",
  language = "en",
  onNavigate = () => {},
  onChangeTier = () => {},
  onNextVisitor = () => {},
}) {
  const [photoStats, setPhotoStats] = useState(null);

  useEffect(() => {
    let active = true;

    getRealOrAIPhotoRound()
      .then((photoRound) =>
        getExhibitAttemptStats({
          stationId: "real-or-ai",
          itemId: photoRound.id,
          contentVersion: language === "es" ? `${photoRound.contentVersion}_es` : photoRound.contentVersion,
        })
      )
      .then((data) => { if (active) setPhotoStats(data); })
      .catch(() => {});

    return () => { active = false; };
  }, [language]);
  const heading = tx(
    language,
    tier === "el"
      ? "Pick a game!"
      : tier === "ms"
      ? "Pick a game."
      : "Which question is yours?"
  );

  const subtitle = tx(
    language,
    tier === "el"
      ? "Start anywhere you like. Every game is short."
      : tier === "ms"
      ? "Start anywhere. There is no set order."
      : "Start anywhere — there is no required path."
  );

  const tierLabel = tx(
    language,
    tier === "el"
      ? "Elementary school"
      : tier === "ms"
      ? "Middle school"
      : "High school & adults"
  );

  return (
    <View style={styles.container}>
      <View style={styles.topBar}>
        <Text style={styles.brand}>
          {tx(language, "AI · Without the Hype or Fear")}
        </Text>
      </View>

      <View style={styles.content}>
        <Text style={styles.heading}>{heading}</Text>

        <Text style={styles.subtitle}>{subtitle}</Text>

        <View style={styles.mainGrid}>
          <Pressable
            onPress={() => onNavigate("real-or-ai")}
            style={({ pressed }) => [
              styles.heroTile,
              pressed && styles.pressed,
            ]}
          >
            <Text style={styles.heroIcon}>🎭</Text>

            <View style={styles.startBadge}>
              <Text style={styles.startBadgeText}>
                {tx(language, "START HERE · 2 MIN")}
              </Text>
            </View>

            <Text style={styles.heroTitle}>{tx(language, "Real or AI?")}</Text>

            <Text style={styles.heroSub}>
              {photoStats?.scored_attempts >= 5 ? (
                <>
                  <Text style={styles.accent}>{photoStats.pct_incorrect}%</Text>
                  {language === "es"
                    ? ` de ${photoStats.scored_attempts} intentos fueron engañados. ¿Puedes superarlo?`
                    : ` of ${photoStats.scored_attempts} attempts were fooled. Can you beat that?`}
                </>
              ) : (
                language === "es"
                  ? `${photoStats?.scored_attempts || 0} intentos reales hasta ahora · haz tu elección.`
                  : `${photoStats?.scored_attempts || 0} live attempts so far · make your guess.`
              )}
            </Text>
          </Pressable>

          {PRIMARY_GAMES.map((item) => (
            <PrimaryTile
              key={item.key}
              item={item}
              onPress={onNavigate}
              language={language}
            />
          ))}
        </View>

        <Text style={styles.moreLabel}>{tx(language, "MORE GAMES")}</Text>

        <View style={styles.smallGrid}>
          {MORE_GAMES.map((item) => (
            <SmallTile
              key={item.key}
              item={item}
              onPress={onNavigate}
              language={language}
            />
          ))}
        </View>

        <View style={styles.links}>
          <Pressable onPress={() => onNavigate("scoreboard")}>
            <Text style={styles.link}>
              {tx(language, "📊 How is the exhibit doing?")}
            </Text>
          </Pressable>

          {tier !== "el" && (
            <Pressable onPress={() => onNavigate("library")}>
              <Text style={styles.link}>
                {tx(language, "📚 Dig deeper — the reading library")}
              </Text>
            </Pressable>
          )}

          <Pressable onPress={onChangeTier}>
            <Text style={styles.link}>
              {language === "es" ? `👤 Jugando como: ${tierLabel} · cambiar` : `👤 Playing as: ${tierLabel} · change`}
            </Text>
          </Pressable>

          <Pressable onPress={onNextVisitor}>
            <Text style={styles.link}>
              {tx(language, "✓ I'm done · next visitor")}
            </Text>
          </Pressable>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 1920,
    height: 1080,
    backgroundColor: "#070D17",
  },

  topBar: {
    height: 64,
    paddingHorizontal: 56,
    justifyContent: "center",
  },

  brand: {
    fontSize: 19,
    fontWeight: "800",
    color: "#8FA5BC",
    letterSpacing: 0.8,
  },

  content: {
    paddingHorizontal: 110,
  },

  heading: {
    marginTop: 30,
    fontSize: 64,
    lineHeight: 70,
    fontWeight: "900",
    color: "#F4F8FC",
  },

  subtitle: {
    marginTop: 8,
    fontSize: 24,
    color: "#9FB2C6",
  },

  mainGrid: {
    marginTop: 28,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 20,
  },

  heroTile: {
    width: 830,
    minHeight: 200,
    paddingHorizontal: 28,
    paddingVertical: 26,
    borderWidth: 2,
    borderColor: "#4FD1FF",
    borderRadius: 22,
    backgroundColor: "#0E2237",
    position: "relative",
    justifyContent: "flex-end",
  },

  heroIcon: {
    position: "absolute",
    left: 28,
    top: 26,
    fontSize: 44,
  },

  startBadge: {
    position: "absolute",
    right: 28,
    top: 26,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 999,
    backgroundColor: "#4FD1FF",
  },

  startBadgeText: {
    fontSize: 18,
    fontWeight: "900",
    color: "#03121F",
  },

  heroTitle: {
    fontSize: 38,
    fontWeight: "900",
    color: "#F4F8FC",
  },

  heroSub: {
    marginTop: 6,
    fontSize: 20,
    color: "#9FB2C6",
  },

  accent: {
    color: "#4FD1FF",
    fontWeight: "900",
  },

  primaryTile: {
    width: 260,
    minHeight: 200,
    paddingHorizontal: 28,
    paddingVertical: 26,
    borderWidth: 1,
    borderColor: "#1B2A44",
    borderRadius: 22,
    backgroundColor: "#0D1626",
    justifyContent: "flex-end",
  },

  primaryIcon: {
    position: "absolute",
    left: 28,
    top: 26,
    fontSize: 44,
  },

  primaryTitle: {
    fontSize: 27,
    lineHeight: 31,
    fontWeight: "900",
    color: "#F4F8FC",
  },

  primaryTime: {
    marginTop: 6,
    fontSize: 17,
    fontWeight: "700",
    color: "#5F7690",
  },

  moreLabel: {
    marginTop: 26,
    fontSize: 16,
    fontWeight: "900",
    letterSpacing: 2,
    color: "#5F7690",
  },

  smallGrid: {
    marginTop: 12,
    flexDirection: "row",
    gap: 14,
  },

  smallTile: {
    width: 220,
    minHeight: 140,
    paddingHorizontal: 22,
    paddingVertical: 20,
    borderWidth: 1,
    borderColor: "#1B2A44",
    borderRadius: 20,
    backgroundColor: "#0D1626",
    justifyContent: "flex-end",
  },

  smallIcon: {
    position: "absolute",
    left: 22,
    top: 20,
    fontSize: 32,
  },

  smallTitle: {
    fontSize: 21,
    lineHeight: 24,
    fontWeight: "900",
    color: "#F4F8FC",
  },

  links: {
    marginTop: 22,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 28,
    alignItems: "center",
  },

  link: {
    fontSize: 19,
    fontWeight: "700",
    color: "#8FA5BC",
  },

  pressed: {
    opacity: 0.9,
  },
});